import { Session } from 'next-auth';
import { apiService } from '@/lib/services/api.service';
import { API_CONFIG } from '@/lib/config/api.config';

// Interfaces
export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  dailyActiveUsers: number;
  monthlyActiveUsers: number;
  averageSessionDuration: number;
  userEngagement: number;
  retentionRate: number;
  totalQuestions: number;
  activeConversations: number;
  totalConversations: number;
  totalRevenue: number;
  activeSubscriptions: number;
}

export interface ActivityItem {
  type: string;
  user: string;
  amount?: number;
  timestamp: string;
}

export interface Metrics {
  userGrowth: number;
  revenueGrowth: number;
  activeUsersGrowth: number;
}

export interface DashboardMetrics {
  userGrowth: number;
  dauGrowth: number;
  mauGrowth: number;
  sessionDurationGrowth: number;
  engagementGrowth: number;
  retentionGrowth: number;
  questionGrowth: number;
  conversationGrowth: number;
  revenueGrowth: number;
  activeUsersGrowth: number;
}

export interface SystemHealth {
  systemUptime: string;
  errorRate: string;
  apiLatency: string;
  averageResponseTime: string;
  messagesPerUser: number;
  userSatisfaction: number;
}

export interface ChartDataPoint {
  x: string | number;
  y: number;
}

export interface PieChartDataPoint {
  name: string;
  value: number;
}

export interface DashboardAnalytics {
  deviceUsage: PieChartDataPoint[];
  browserDistribution: PieChartDataPoint[];
  questionCategories: ChartDataPoint[];
  successRates: ChartDataPoint[];
  retentionTrend: ChartDataPoint[];
  sessionDurationTrend: ChartDataPoint[];
  feedbackStats: {
    positive: number;
    negative: number;
    total: number;
    usageRate: number;
    topReasons: ChartDataPoint[];
  };
  documentInteractions: {
    legalSourceClicks: number;
    ccqArticleClicks: number;
    suggestedQuestionClicks: number;
  };
  timeDistribution: {
    hourly: ChartDataPoint[];
    daily: ChartDataPoint[];
    weekly: ChartDataPoint[];
  };
  conversationStats: {
    averageLength: number;
    distribution: ChartDataPoint[];
  };
}

export interface DashboardOverview {
  stats: DashboardStats;
  metrics: DashboardMetrics;
  systemHealth: SystemHealth;
  analytics: DashboardAnalytics;
  recentActivity: ActivityItem[];
}

export interface User {
  city: string;
  language: string;
  ageGroup: string;
  acceptedPolicies: boolean;
  userType: string;
  status: string;
  isAdmin: boolean;
  lastLogin: string | number | Date;
  role: string;
  id: string;
  email: string;
  name: string;
  cognitoStatus: string;
  subscriptionStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface AuditLogEntry {
  id: string;
  type: string;
  entityId?: string;
  action: string;
  changes: Record<string, any>;
  user: string;
  userName?: string;
  userId?: string;
  timestamp: string;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface Payment {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'refunded';
  refundedAmount?: number;
  reason?: string;
  stripePaymentId: string;
  createdAt: string;
}

export interface Subscription {
  id: string;
  userId: string;
  user: {
    email: string;
    name: string;
  };
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: 'free' | 'active' | 'cancelled' | 'past_due';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  priceId: string;
  createdAt: string;
  updatedAt: string;
  payments?: Payment[];
}

export interface PaymentAnalytics {
  totalRevenue: number;
  refundedAmount: number;
  netRevenue: number;
  failedPayments: number;
  currency: string;
}

export interface SubscriptionRevenue {
  monthlyRevenue: number;
  totalRevenue: number;
  activeSubscriptions: number;
  currency: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AdminOverview {
  stats: {
    totalUsers: number;
    activeUsers: number;
    totalAdvices: number;
    totalFlaggedResponses: number;
    totalRevenue: number;
    activeSubscriptions: number;
  };
  recentActivity: {
    type: string;
    user: string;
    timestamp: string;
  }[];
  metrics: {
    userGrowth: number;
    adviceGrowth: number;
    activeUsersPercent: number;
    revenueGrowth: number;
  };
}

export interface ChatHistory {
  id: string;
  title: string;
  description: string;
  responses: {
    question: string;
    answer: string;
    timestamp: string;
  }[];
  createdAt: string;
}

export interface AuditLog {
  id: string;
  type: string;
  action: string;
  changes: any;
  user: string;
  timestamp: string;
}

export interface FlaggedResponse {
  id: number;
  email: string;
  reaction: string;
  question: string;
  response: string;
  comments?: string;
  timestamp: string;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Request deduplication map
const pendingRequests = new Map<string, Promise<any>>();

// Cache map with TTL
const cache = new Map<string, CacheEntry<any>>();

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export const getMockDashboardOverview = (): DashboardOverview => ({
  stats: {
    totalUsers: 1250,
    activeUsers: 850,
    totalRevenue: 25000,
    activeSubscriptions: 450,
    totalConversations: 3500,
    monthlyActiveUsers: 1000,
    averageSessionDuration: 10,
    retentionRate: 0.8,
    totalQuestions: 1000,
    activeConversations: 120,
    dailyActiveUsers: 300,
    userEngagement: 75,
  },
  metrics: {
    userGrowth: 15,
    revenueGrowth: 25,
    activeUsersGrowth: 20,
    conversationGrowth: 30,
    dauGrowth: 12,
    engagementGrowth: 8,
    mauGrowth: 10,
    sessionDurationGrowth: 5,
    retentionGrowth: 15,
    questionGrowth: 20,
  },
  systemHealth: {
    systemUptime: '99.9%',
    errorRate: '0.1%',
    apiLatency: '150ms',
    averageResponseTime: '2.5s',
    messagesPerUser: 25,
    userSatisfaction: 95,
  },
  analytics: {
    deviceUsage: [],
    browserDistribution: [],
    questionCategories: [],
    successRates: [],
    retentionTrend: [],
    sessionDurationTrend: [],
    feedbackStats: {
      positive: 0,
      negative: 0,
      total: 0,
      usageRate: 0,
      topReasons: [],
    },
    documentInteractions: {
      legalSourceClicks: 0,
      ccqArticleClicks: 0,
      suggestedQuestionClicks: 0,
    },
    timeDistribution: {
      hourly: [],
      daily: [],
      weekly: [],
    },
    conversationStats: {
      averageLength: 0,
      distribution: [],
    },
  },
  recentActivity: [],
});

export const getMockFlaggedResponses = (): FlaggedResponse[] => [
  {
    id: 1,
    email: 'user1@example.com',
    reaction: 'negative',
    question: 'What are my rights when dealing with an unresponsive landlord?',
    response:
      'According to tenant laws, you have several options when dealing with an unresponsive landlord...',
    comments: "The information wasn't specific to my province.",
    timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
  },
  {
    id: 2,
    email: 'user2@example.com',
    reaction: 'positive',
    question: 'How do I file a small claims court case?',
    response:
      'To file a small claims court case, you need to follow these steps...',
    comments: 'Very helpful advice, thank you!',
    timestamp: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
  },
  {
    id: 3,
    email: 'user3@example.com',
    reaction: 'negative',
    question: 'What is the standard notice period for terminating a lease?',
    response:
      'The standard notice period for terminating a lease varies by jurisdiction...',
    comments:
      "This didn't answer my specific question about breaking a lease early.",
    timestamp: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
  },
  {
    id: 4,
    email: 'user4@example.com',
    reaction: 'positive',
    question: 'How can I dispute a traffic ticket?',
    response:
      'There are several ways to dispute a traffic ticket depending on your circumstances...',
    timestamp: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
  },
];

class AdminService {
  private dedupeRequest<T>(key: string, request: Promise<T>): Promise<T> {
    const pending = pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    pendingRequests.set(key, request);
    return request.finally(() => {
      pendingRequests.delete(key);
    });
  }

  private getCacheKey(key: string, session: Session | null): string {
    return `${session?.user?.email || 'anonymous'}-${key}`;
  }

  private getFromCache<T>(key: string, session: Session | null): T | null {
    const cacheKey = this.getCacheKey(key, session);
    const cached = cache.get(cacheKey);

    if (!cached) return null;

    // Check if cache is expired
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      cache.delete(cacheKey);
      return null;
    }

    return cached.data as T;
  }

  private setCache<T>(key: string, session: Session | null, data: T): void {
    const cacheKey = this.getCacheKey(key, session);
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
  }

  clearCache(): void {
    cache.clear();
    pendingRequests.clear();
  }

  async getOverview(session: Session): Promise<DashboardOverview> {
    const key = 'overview';
    const cached = this.getFromCache<DashboardOverview>(key, session);
    if (cached) return cached;

    const response = await this.dedupeRequest(
      key,
      apiService.get<DashboardOverview>(
        API_CONFIG.ENDPOINTS.ADMIN.OVERVIEW,
        session
      )
    );

    const data = response || {
      stats: {
        totalUsers: 0,
        activeUsers: 0,
        totalRevenue: 0,
        activeSubscriptions: 0,
        totalConversations: 0,
        activeConversations: 0,
        dailyActiveUsers: 0,
        userEngagement: 0,
      },
      recentActivity: [],
      metrics: { userGrowth: 0, revenueGrowth: 0, activeUsersGrowth: 0 },
      systemHealth: {
        systemUptime: '99.9%',
        errorRate: '0.1%',
        apiLatency: '150ms',
        averageResponseTime: '2.5s',
        messagesPerUser: 25,
        userSatisfaction: 95,
      },
    };

    this.setCache(key, session, data);
    return data as DashboardOverview;
  }

  async getUsers(session: Session): Promise<User[]> {
    const key = 'users';
    const cached = this.getFromCache<User[]>(key, session);
    if (cached) return cached;

    const response = await this.dedupeRequest(
      key,
      apiService.get<User[]>(API_CONFIG.ENDPOINTS.ADMIN.USERS, session)
    );

    const data = response || [];
    this.setCache(key, session, data);
    return data;
  }

  async getChatHistory(
    userId: string,
    session: Session
  ): Promise<ChatMessage[]> {
    const key = `chat-history-${userId}`;
    const cached = this.getFromCache<ChatMessage[]>(key, session);
    if (cached) return cached;

    const response = await this.dedupeRequest(
      key,
      apiService.get<ChatMessage[]>(
        API_CONFIG.ENDPOINTS.ADMIN.CHAT_HISTORY(userId),
        session
      )
    );

    const data = response || [];
    this.setCache(key, session, data);
    return data;
  }

  async getAuditLogs(
    session: Session,
    params?: {
      page?: number;
      limit?: number;
      entityType?: string;
      action?: string;
      userId?: string;
      startDate?: Date;
      endDate?: Date;
      searchQuery?: string;
      sortField?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<AuditLogResponse> {
    const key = 'audit-logs';

    // Don't use cache when parameters are provided
    const useCache = !params || Object.keys(params).length === 0;
    const cached = useCache
      ? this.getFromCache<AuditLogResponse>(key, session)
      : null;
    if (cached) return cached;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.entityType)
        queryParams.append('entityType', params.entityType);
      if (params.action) queryParams.append('action', params.action);
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.startDate)
        queryParams.append('startDate', params.startDate.toISOString());
      if (params.endDate)
        queryParams.append('endDate', params.endDate.toISOString());
      if (params.searchQuery)
        queryParams.append('searchQuery', params.searchQuery);
      if (params.sortField) queryParams.append('sortField', params.sortField);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : '';
    const url = `${API_CONFIG.ENDPOINTS.ADMIN.AUDIT_LOGS}${queryString}`;

    const response = await this.dedupeRequest(
      key,
      apiService.get<AuditLogResponse>(url, session)
    );

    const data = response || {
      items: [],
      meta: { total: 0, page: 1, limit: 10, totalPages: 0 },
    };

    // Only cache if no parameters were provided
    if (useCache) {
      this.setCache(key, session, data);
    }

    return data;
  }

  async getPayments(
    session: Session,
    page: number = 1,
    limit: number = 10,
    status?: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<PaginatedResponse<Payment>> {
    try {
      const key = `payments-${page}-${limit}-${status}-${startDate}-${endDate}`;
      const cached = this.getFromCache<PaginatedResponse<Payment>>(
        key,
        session
      );
      if (cached) return cached;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status }),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      });

      console.log(`Fetching payments with params: ${queryParams.toString()}`);

      const response = await this.dedupeRequest(
        key,
        apiService.get<PaginatedResponse<Payment>>(
          `${API_CONFIG.ENDPOINTS.ADMIN.PAYMENTS}?${queryParams}`,
          session
        )
      );

      const data = response || {
        data: [],
        meta: { total: 0, page, limit, totalPages: 0 },
      };
      this.setCache(key, session, data);
      return data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      // Return empty data instead of throwing an error
      return {
        data: [],
        meta: { total: 0, page, limit, totalPages: 0 },
      };
    }
  }

  async getFailedPayments(
    session: Session,
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<Payment>> {
    return this.getPayments(session, page, limit, 'failed');
  }

  async getRefunds(
    session: Session,
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<Payment>> {
    const key = `refunds-${page}-${limit}`;
    const cached = this.getFromCache<PaginatedResponse<Payment>>(key, session);
    if (cached) return cached;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await this.dedupeRequest(
      key,
      apiService.get<PaginatedResponse<Payment>>(
        `${API_CONFIG.ENDPOINTS.ADMIN.PAYMENTS}/refunds?${queryParams}`,
        session
      )
    );

    const data = response || {
      data: [],
      meta: { total: 0, page, limit, totalPages: 0 },
    };
    this.setCache(key, session, data);
    return data;
  }

  async issueRefund(
    session: Session,
    paymentId: string,
    amount?: number,
    reason?: string
  ): Promise<any> {
    const key = `refund-${paymentId}`;
    const response = await this.dedupeRequest(
      key,
      apiService.post(
        `${API_CONFIG.ENDPOINTS.ADMIN.PAYMENTS}/${paymentId}/refund`,
        { amount, reason },
        session
      )
    );

    // Invalidate relevant caches
    this.clearCache();
    return response;
  }

  async getSubscriptions(
    session: Session,
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<Subscription>> {
    const key = `subscriptions-${page}-${limit}`;
    const cached = this.getFromCache<PaginatedResponse<Subscription>>(
      key,
      session
    );
    if (cached) return cached;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await this.dedupeRequest(
      key,
      apiService.get<PaginatedResponse<Subscription>>(
        `${API_CONFIG.ENDPOINTS.ADMIN.SUBSCRIPTIONS}?${queryParams}`,
        session
      )
    );

    const data = response || {
      data: [],
      meta: { total: 0, page, limit, totalPages: 0 },
    };
    this.setCache(key, session, data);
    return data;
  }

  async getSubscriptionRevenue(session: Session): Promise<SubscriptionRevenue> {
    const key = 'subscription-revenue';
    const cached = this.getFromCache<SubscriptionRevenue>(key, session);
    if (cached) return cached;

    const response = await this.dedupeRequest(
      key,
      apiService.get<SubscriptionRevenue>(
        API_CONFIG.ENDPOINTS.ADMIN.SUBSCRIPTIONS_REVENUE,
        session
      )
    );

    const data = response || {
      monthlyRevenue: 0,
      totalRevenue: 0,
      activeSubscriptions: 0,
      currency: 'USD',
    };
    this.setCache(key, session, data);
    return data;
  }

  async updateUser(
    userId: string,
    userData: Partial<User>,
    session: Session
  ): Promise<User> {
    const key = `update-user-${userId}`;
    const response = await this.dedupeRequest(
      key,
      apiService.put<User>(
        API_CONFIG.ENDPOINTS.ADMIN.USER(userId),
        userData,
        session
      )
    );

    if (!response) {
      throw new Error('Failed to update user');
    }

    // Invalidate users cache after update
    const usersKey = 'users';
    const cacheKey = this.getCacheKey(usersKey, session);
    cache.delete(cacheKey);

    return response;
  }

  async disableUser(userId: string, session: Session): Promise<void> {
    const key = `disable-user-${userId}`;
    await this.dedupeRequest(
      key,
      apiService.patch(
        API_CONFIG.ENDPOINTS.ADMIN.USER(userId),
        { status: 'SUSPENDED' },
        session
      )
    );

    // Invalidate users cache after delete
    const usersKey = 'users';
    const cacheKey = this.getCacheKey(usersKey, session);
    cache.delete(cacheKey);
  }

  async getDashboardOverview(session: Session): Promise<DashboardOverview> {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_NEXT_API_URL}/admin/overview`,
        {
          headers: {
            Authorization: `Bearer ${session?.user?.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard overview');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      return getMockDashboardOverview();
    }
  }

  async getFlaggedResponses(session: Session): Promise<FlaggedResponse[]> {
    const key = 'flagged-responses';
    const cached = this.getFromCache<FlaggedResponse[]>(key, session);
    if (cached) return cached;

    try {
      const response = await this.dedupeRequest(
        key,
        apiService.get<FlaggedResponse[]>(
          API_CONFIG.ENDPOINTS.ADMIN.FLAGGED_RESPONSES,
          session
        )
      );

      this.setCache(key, session, response);
      return response || [];
    } catch (error) {
      console.error('Error fetching flagged responses:', error);
      console.warn('Using mock flagged responses data');
      return getMockFlaggedResponses();
    }
  }
}

export const adminService = new AdminService();
